import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, addDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { motion, AnimatePresence } from 'framer-motion';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('enquiries');
    const [enquiries, setEnquiries] = useState([]);
    const [jobApplications, setJobApplications] = useState([]);

    // Add Project State
    const [projectForm, setProjectForm] = useState({
        title: '',
        location: '',
        year: '',
        description: '',
        gallery: [{ src: '', viewTitle: '', caption: '' }],
        additionalGallery: [''],
        videoType: 'local',
        videoSrc: '',
        videoThumbnail: '',
        architects: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

    // Add Blog State
    const [blogForm, setBlogForm] = useState({
        title: '',
        date: '',
        heroImage: '',
        authorName: '',
        authorRole: '',
        authorImage: '',
        contentBlocks: [] // Array of block objects
    });

    const ALLOWED_ADMINS = ['workjahanzaibbhat@gmail.com', 'adismehta@gmail.com'];

    const getAdminName = (email) => {
        if (email === 'adismehta@gmail.com') return 'Aditya Mehta';
        if (email === 'workjahanzaibbhat@gmail.com') return 'Jahanzaib';
        return 'Admin';
    };

    useEffect(() => {
        const unsubscribeAuth = auth.onAuthStateChanged((currentUser) => {
            if (currentUser) {
                if (ALLOWED_ADMINS.includes(currentUser.email)) {
                    setUser(currentUser);
                } else {
                    signOut(auth).then(() => navigate('/x3k9-admin-login'));
                }
            } else {
                navigate('/x3k9-admin-login');
            }
            setLoading(false);
        });

        const qEnquiries = query(collection(db, 'enquiries'), orderBy('createdAt', 'desc'));
        const unsubscribeEnquiries = onSnapshot(qEnquiries, (snapshot) => {
            setEnquiries(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const qJobs = query(collection(db, 'jobApplications'), orderBy('createdAt', 'desc'));
        const unsubscribeJobs = onSnapshot(qJobs, (snapshot) => {
            setJobApplications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => {
            unsubscribeAuth();
            unsubscribeEnquiries();
            unsubscribeJobs();
        };
    }, [navigate]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            navigate('/x3k9-admin-login');
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    const toggleStatus = async (collectionName, id, currentStatus) => {
        const newStatus = currentStatus === 'unread' ? 'read' : 'unread';
        const docRef = doc(db, collectionName, id);
        await updateDoc(docRef, { status: newStatus });
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        return timestamp.toDate().toLocaleString();
    };

    // Project Form Handlers
    const handleProjectChange = (e) => {
        const { name, value } = e.target;
        setProjectForm(prev => ({ ...prev, [name]: value }));
    };

    const handleGalleryChange = (index, field, value) => {
        const newGallery = [...projectForm.gallery];
        newGallery[index][field] = value;
        setProjectForm(prev => ({ ...prev, gallery: newGallery }));
    };

    const addGalleryItem = () => {
        setProjectForm(prev => ({
            ...prev,
            gallery: [...prev.gallery, { src: '', viewTitle: '', caption: '' }]
        }));
    };

    const removeGalleryItem = (index) => {
        const newGallery = projectForm.gallery.filter((_, i) => i !== index);
        setProjectForm(prev => ({ ...prev, gallery: newGallery }));
    };

    const handleAdditionalGalleryChange = (index, value) => {
        const newGallery = [...projectForm.additionalGallery];
        newGallery[index] = value;
        setProjectForm(prev => ({ ...prev, additionalGallery: newGallery }));
    };

    const addAdditionalGalleryItem = () => {
        setProjectForm(prev => ({ ...prev, additionalGallery: [...prev.additionalGallery, ''] }));
    };

    const removeAdditionalGalleryItem = (index) => {
        const newGallery = projectForm.additionalGallery.filter((_, i) => i !== index);
        setProjectForm(prev => ({ ...prev, additionalGallery: newGallery }));
    };

    const handleProjectSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitMessage({ type: '', text: '' });

        try {
            const cleanGallery = projectForm.gallery.filter(item => item.src.trim() !== '');
            const cleanAdditionalGallery = projectForm.additionalGallery.filter(src => src.trim() !== '');

            const projectData = {
                title: projectForm.title,
                location: projectForm.location,
                year: projectForm.year,
                description: projectForm.description,
                gallery: cleanGallery.length > 0 ? cleanGallery : null,
                additionalGallery: cleanAdditionalGallery.length > 0 ? cleanAdditionalGallery : null,
                video: projectForm.videoSrc ? {
                    type: projectForm.videoType,
                    src: projectForm.videoSrc,
                    thumbnail: projectForm.videoThumbnail
                } : null,
                architects: projectForm.architects ? projectForm.architects.split(',').map(a => a.trim()) : null,
                createdAt: new Date()
            };

            await addDoc(collection(db, 'projects'), projectData);

            setSubmitMessage({ type: 'success', text: 'Project added successfully!' });

            setProjectForm({
                title: '', location: '', year: '', description: '',
                gallery: [{ src: '', viewTitle: '', caption: '' }],
                additionalGallery: [''],
                videoType: 'local', videoSrc: '', videoThumbnail: '', architects: ''
            });

            setTimeout(() => setSubmitMessage({ type: '', text: '' }), 5000);

        } catch (error) {
            console.error("Error adding project:", error);
            setSubmitMessage({ type: 'error', text: 'Failed to add project. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Blog Form Handlers
    const handleBlogMetadataChange = (e) => {
        const { name, value } = e.target;
        setBlogForm(prev => ({ ...prev, [name]: value }));
    };

    const addBlogBlock = (type) => {
        let newBlock = { id: Date.now(), type };
        if (type === 'text') newBlock.content = '';
        if (type === 'heading') newBlock.content = '';
        if (type === 'quote') newBlock.content = '';
        if (type === 'singleImage') { newBlock.src = ''; newBlock.caption = ''; }
        if (type === 'imageGrid') { newBlock.src1 = ''; newBlock.src2 = ''; }

        setBlogForm(prev => ({ ...prev, contentBlocks: [...prev.contentBlocks, newBlock] }));
    };

    const updateBlogBlock = (id, field, value) => {
        setBlogForm(prev => ({
            ...prev,
            contentBlocks: prev.contentBlocks.map(block =>
                block.id === id ? { ...block, [field]: value } : block
            )
        }));
    };

    const removeBlogBlock = (id) => {
        setBlogForm(prev => ({
            ...prev,
            contentBlocks: prev.contentBlocks.filter(block => block.id !== id)
        }));
    };

    const moveBlogBlock = (index, direction) => {
        const newBlocks = [...blogForm.contentBlocks];
        if (direction === 'up' && index > 0) {
            [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
        } else if (direction === 'down' && index < newBlocks.length - 1) {
            [newBlocks[index + 1], newBlocks[index]] = [newBlocks[index], newBlocks[index + 1]];
        }
        setBlogForm(prev => ({ ...prev, contentBlocks: newBlocks }));
    };

    const handleBlogSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitMessage({ type: '', text: '' });

        try {
            const cleanBlocks = blogForm.contentBlocks.filter(b => {
                if (b.type === 'text' || b.type === 'heading' || b.type === 'quote') return b.content.trim() !== '';
                if (b.type === 'singleImage') return b.src.trim() !== '';
                if (b.type === 'imageGrid') return b.src1.trim() !== '' && b.src2.trim() !== '';
                return false;
            });

            const blogData = {
                title: blogForm.title,
                date: blogForm.date,
                heroImage: blogForm.heroImage,
                author: {
                    name: blogForm.authorName,
                    role: blogForm.authorRole,
                    image: blogForm.authorImage
                },
                contentBlocks: cleanBlocks,
                createdAt: new Date(),
                // Generate a route friendly slug
                slug: blogForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '')
            };

            await addDoc(collection(db, 'blogs'), blogData);

            setSubmitMessage({ type: 'success', text: 'Blog added successfully!' });

            setBlogForm({
                title: '', date: '', heroImage: '', authorName: '', authorRole: '', authorImage: '', contentBlocks: []
            });

            setTimeout(() => setSubmitMessage({ type: '', text: '' }), 5000);

        } catch (error) {
            console.error("Error adding blog:", error);
            setSubmitMessage({ type: 'error', text: 'Failed to add blog. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };



    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-900 text-white flex items-center justify-center">
                Loading...
            </div>
        );
    }

    const TableCell = ({ children, className = "" }) => (
        <td className={`px-4 py-3 align-top break-words max-w-[300px] ${className}`} title={typeof children === 'string' ? children : ''}>
            {children}
        </td>
    );

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen bg-neutral-900 text-white pt-24 px-8 pb-12 overflow-x-hidden"
        >
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8 border-b border-neutral-800 pb-6">
                    <div>
                        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
                        {user && <p className="text-neutral-400 mt-2">Hello {getAdminName(user.email)}</p>}
                    </div>
                    <button
                        onClick={handleLogout}
                        className="bg-neutral-800 hover:bg-neutral-700 text-white px-4 py-2 rounded transition-colors text-sm"
                    >
                        Log Out
                    </button>
                </div>

                <div className="flex space-x-4 mb-6 overflow-x-auto pb-2 scrollbar-none">
                    <button
                        onClick={() => setActiveTab('enquiries')}
                        className={`px-6 py-2 rounded-lg font-medium transition whitespace-nowrap ${activeTab === 'enquiries' ? 'bg-violet-600 text-white' : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'}`}
                    >
                        Enquiries ({enquiries.filter(e => e.status === 'unread').length} New)
                    </button>
                    <button
                        onClick={() => setActiveTab('jobApplications')}
                        className={`px-6 py-2 rounded-lg font-medium transition whitespace-nowrap ${activeTab === 'jobApplications' ? 'bg-violet-600 text-white' : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'}`}
                    >
                        Job Applications ({jobApplications.filter(j => j.status === 'unread').length} New)
                    </button>
                    <button
                        onClick={() => setActiveTab('addProject')}
                        className={`px-6 py-2 rounded-lg font-medium transition whitespace-nowrap ${activeTab === 'addProject' ? 'bg-violet-600 text-white' : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'}`}
                    >
                        Add Project
                    </button>
                    <button
                        onClick={() => setActiveTab('addBlog')}
                        className={`px-6 py-2 rounded-lg font-medium transition whitespace-nowrap ${activeTab === 'addBlog' ? 'bg-violet-600 text-white' : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'}`}
                    >
                        Add Blog
                    </button>
                </div>

                {(activeTab === 'enquiries' || activeTab === 'jobApplications') && (
                    <div className="bg-neutral-950 rounded-lg shadow-xl overflow-hidden border border-neutral-800">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm text-neutral-300">
                                <thead className="text-xs uppercase bg-neutral-900 text-neutral-400 border-b border-neutral-800">
                                    <tr>
                                        <th className="px-4 py-4 w-40">Date</th>
                                        <th className="px-4 py-4">Name</th>
                                        <th className="px-4 py-4">Email</th>
                                        {activeTab === 'enquiries' && <th className="px-4 py-4">Subject</th>}
                                        {activeTab === 'jobApplications' && <>
                                            <th className="px-4 py-4">Phone</th>
                                            <th className="px-4 py-4">Position</th>
                                            <th className="px-4 py-4">Resume</th>
                                        </>}
                                        <th className="px-4 py-4 max-w-xs">Message</th>
                                        <th className="px-4 py-4 text-center">Status</th>
                                        <th className="px-4 py-4 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <AnimatePresence>
                                        {(activeTab === 'enquiries' ? enquiries : jobApplications).map((item) => (
                                            <motion.tr
                                                key={item.id}
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0 }}
                                                className={`border-b border-neutral-800/50 transition-colors ${item.status === 'unread' ? 'bg-green-900/20' : 'bg-neutral-800/30'}`}
                                            >
                                                <TableCell>{formatDate(item.createdAt)}</TableCell>
                                                <TableCell className="font-medium text-white">{item.name}</TableCell>
                                                <TableCell>{item.email}</TableCell>

                                                {activeTab === 'enquiries' && (
                                                    <TableCell>{item.subject}</TableCell>
                                                )}

                                                {activeTab === 'jobApplications' && (
                                                    <>
                                                        <TableCell>{item.countryCode} {item.phone}</TableCell>
                                                        <TableCell>{item.position}</TableCell>
                                                        <TableCell>
                                                            {item.resumeUrl ? (
                                                                <a href={item.resumeUrl} target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 underline">
                                                                    Download
                                                                </a>
                                                            ) : (
                                                                <span className="text-neutral-500">None</span>
                                                            )}
                                                        </TableCell>
                                                    </>
                                                )}

                                                <TableCell className="whitespace-normal min-w-[200px] text-neutral-400">
                                                    {item.message}
                                                </TableCell>

                                                <td className="px-4 py-3 text-center">
                                                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${item.status === 'unread' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-neutral-700 text-neutral-300 border-neutral-600'}`}>
                                                        {item.status.toUpperCase()}
                                                    </span>
                                                </td>

                                                <td className="px-4 py-3 text-center">
                                                    <button
                                                        onClick={() => toggleStatus(activeTab === 'enquiries' ? 'enquiries' : 'jobApplications', item.id, item.status)}
                                                        className={`px-3 py-1.5 rounded text-xs transition ${item.status === 'unread' ? 'bg-neutral-700 hover:bg-neutral-600 text-white' : 'bg-violet-600 hover:bg-violet-500 text-white'}`}
                                                    >
                                                        {item.status === 'unread' ? 'Mark as Read' : 'Mark as Unread'}
                                                    </button>
                                                </td>
                                            </motion.tr>
                                        ))}
                                    </AnimatePresence>
                                    {(activeTab === 'enquiries' ? enquiries : jobApplications).length === 0 && (
                                        <tr>
                                            <td colSpan="8" className="px-6 py-12 text-center text-neutral-500">
                                                No {activeTab === 'enquiries' ? 'enquiries' : 'job applications'} found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'addProject' && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-neutral-950 rounded-lg shadow-xl p-8 border border-neutral-800"
                    >
                        <h2 className="text-2xl font-semibold mb-6">Add New Project</h2>
                        {submitMessage.text && (
                            <div className={`p-4 mb-6 rounded-lg ${submitMessage.type === 'success' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
                                {submitMessage.text}
                            </div>
                        )}
                        <form onSubmit={handleProjectSubmit} className="space-y-8">
                            {/* Basic Info */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-neutral-300 border-b border-neutral-800 pb-2">Basic Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm text-neutral-400 mb-1">Title *</label>
                                        <input required type="text" name="title" value={projectForm.title} onChange={handleProjectChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="e.g. Residence Moira, Goa" />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-neutral-400 mb-1">Location *</label>
                                        <input required type="text" name="location" value={projectForm.location} onChange={handleProjectChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="e.g. Moira, North Goa" />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-neutral-400 mb-1">Year *</label>
                                        <input required type="text" name="year" value={projectForm.year} onChange={handleProjectChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="e.g. 2024" />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-neutral-400 mb-1">Architects</label>
                                        <input type="text" name="architects" value={projectForm.architects} onChange={handleProjectChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="Comma separated, e.g. Aditya Mehta, Jahanzaib Bhat" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm text-neutral-400 mb-1">Description</label>
                                        <textarea name="description" value={projectForm.description} onChange={handleProjectChange} rows="3" className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="Project description..."></textarea>
                                    </div>
                                </div>
                            </div>

                            {/* Main Gallery */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-neutral-300 border-b border-neutral-800 pb-2">Main Gallery (Narrative)</h3>
                                <p className="text-xs text-neutral-500">The first image will be used as the Hero Background & Thumbnail.</p>

                                {projectForm.gallery.map((img, index) => (
                                    <div key={index} className="bg-neutral-900 p-4 border border-neutral-800 rounded-lg relative">
                                        <div className="absolute top-4 right-4 text-xs text-neutral-500">Image {index + 1}</div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="md:col-span-2">
                                                <label className="block text-sm text-neutral-400 mb-1">Image URL *</label>
                                                <input required type="text" value={img.src} onChange={(e) => handleGalleryChange(index, 'src', e.target.value)} className="w-full bg-neutral-950 border border-neutral-700 rounded p-2 text-white focus:outline-none focus:border-violet-500 transition" placeholder="/images/projects/your-project/thumb.webp" />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-neutral-400 mb-1">View Title</label>
                                                <input type="text" value={img.viewTitle} onChange={(e) => handleGalleryChange(index, 'viewTitle', e.target.value)} className="w-full bg-neutral-950 border border-neutral-700 rounded p-2 text-white focus:outline-none focus:border-violet-500 transition" placeholder="e.g. Backyard View" />
                                            </div>
                                            <div>
                                                <label className="block text-sm text-neutral-400 mb-1">Caption</label>
                                                <input type="text" value={img.caption} onChange={(e) => handleGalleryChange(index, 'caption', e.target.value)} className="w-full bg-neutral-950 border border-neutral-700 rounded p-2 text-white focus:outline-none focus:border-violet-500 transition" placeholder="e.g. Entrance courtyard shaded by..." />
                                            </div>
                                        </div>
                                        {projectForm.gallery.length > 1 && (
                                            <button type="button" onClick={() => removeGalleryItem(index)} className="mt-4 text-sm text-red-400 hover:text-red-300 transition">Remove Image</button>
                                        )}
                                    </div>
                                ))}

                                <button type="button" onClick={addGalleryItem} className="text-sm bg-neutral-800 hover:bg-neutral-700 text-neutral-200 px-4 py-2 rounded transition border border-neutral-700">
                                    + Add Another Gallery Image
                                </button>
                            </div>

                            {/* Additional Gallery */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-neutral-300 border-b border-neutral-800 pb-2">Project Gallery (Grid)</h3>

                                {projectForm.additionalGallery.map((src, index) => (
                                    <div key={index} className="flex space-x-2">
                                        <input type="text" value={src} onChange={(e) => handleAdditionalGalleryChange(index, e.target.value)} className="flex-1 bg-neutral-900 border border-neutral-700 rounded p-2 text-white focus:outline-none focus:border-violet-500 transition" placeholder={`/images/projects/your-project/${index + 1}.webp`} />
                                        {projectForm.additionalGallery.length > 1 && (
                                            <button type="button" onClick={() => removeAdditionalGalleryItem(index)} className="px-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded transition border border-red-500/20">X</button>
                                        )}
                                    </div>
                                ))}

                                <button type="button" onClick={addAdditionalGalleryItem} className="text-sm bg-neutral-800 hover:bg-neutral-700 text-neutral-200 px-4 py-2 rounded transition border border-neutral-700">
                                    + Add Additional Image URL
                                </button>
                            </div>

                            {/* Video */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-neutral-300 border-b border-neutral-800 pb-2">Video Walkthrough (Optional)</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm text-neutral-400 mb-1">Video Type</label>
                                        <select name="videoType" value={projectForm.videoType} onChange={handleProjectChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition appearance-none">
                                            <option value="local">Local MP4</option>
                                            <option value="youtube">YouTube</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm text-neutral-400 mb-1">Video Source</label>
                                        <input type="text" name="videoSrc" value={projectForm.videoSrc} onChange={handleProjectChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="URL or YouTube ID" />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-neutral-400 mb-1">Thumbnail URL</label>
                                        <input type="text" name="videoThumbnail" value={projectForm.videoThumbnail} onChange={handleProjectChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="/images/projects/your-project/thumb.webp" />
                                    </div>
                                </div>
                            </div>

                            {/* Submit */}
                            <div className="pt-6 border-t border-neutral-800">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full py-4 rounded-lg font-semibold tracking-wide text-lg transition ${isSubmitting ? 'bg-violet-800 text-white/50 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700 text-white'}`}
                                >
                                    {isSubmitting ? 'Saving Project...' : 'Save Project to Database'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                )}

                {activeTab === 'addBlog' && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-neutral-950 rounded-lg shadow-xl p-8 border border-neutral-800"
                    >
                        <h2 className="text-2xl font-semibold mb-6">Write New Blog</h2>
                        {submitMessage.text && (
                            <div className={`p-4 mb-6 rounded-lg ${submitMessage.type === 'success' ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
                                {submitMessage.text}
                            </div>
                        )}

                        <form onSubmit={handleBlogSubmit} className="space-y-8">
                            {/* Metadata */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-neutral-300 border-b border-neutral-800 pb-2">Article Metadata</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm text-neutral-400 mb-1">Title *</label>
                                        <input required type="text" name="title" value={blogForm.title} onChange={handleBlogMetadataChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="e.g. Undoing through the cracking concrete" />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-neutral-400 mb-1">Date Display String *</label>
                                        <input required type="text" name="date" value={blogForm.date} onChange={handleBlogMetadataChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="e.g. December 24, 2025" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm text-neutral-400 mb-1">Hero Image URL *</label>
                                        <input required type="text" name="heroImage" value={blogForm.heroImage} onChange={handleBlogMetadataChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="/images/blogs/your-blog/hero.webp" />
                                    </div>
                                </div>
                            </div>

                            {/* Author */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-neutral-300 border-b border-neutral-800 pb-2">Author Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm text-neutral-400 mb-1">Name *</label>
                                        <input required type="text" name="authorName" value={blogForm.authorName} onChange={handleBlogMetadataChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="e.g. Aditya Mehta" />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-neutral-400 mb-1">Role *</label>
                                        <input required type="text" name="authorRole" value={blogForm.authorRole} onChange={handleBlogMetadataChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="e.g. Project Architect, Studio Sharan" />
                                    </div>
                                    <div>
                                        <label className="block text-sm text-neutral-400 mb-1">Author Image URL *</label>
                                        <input required type="text" name="authorImage" value={blogForm.authorImage} onChange={handleBlogMetadataChange} className="w-full bg-neutral-900 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="/images/team/aditya-mehta.png" />
                                    </div>
                                </div>
                            </div>

                            {/* Dynamic Blocks */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-neutral-300 border-b border-neutral-800 pb-2 flex justify-between items-end">
                                    <span>Article Content Builder</span>
                                </h3>

                                {blogForm.contentBlocks.length === 0 && (
                                    <div className="text-neutral-500 text-sm italic py-4">No content blocks yet. Add a text paragraph, heading, or image below.</div>
                                )}

                                <div className="space-y-6">
                                    {blogForm.contentBlocks.map((block, index) => (
                                        <div key={block.id} className="relative bg-neutral-900 p-5 rounded-xl border border-neutral-800">
                                            {/* Block Controls */}
                                            <div className="absolute top-3 right-3 flex items-center bg-neutral-950 rounded border border-neutral-800 p-0.5 shadow-sm z-10">
                                                <button type="button" onClick={() => moveBlogBlock(index, 'up')} disabled={index === 0} className="p-1.5 text-neutral-400 hover:text-white disabled:opacity-30 transition">
                                                    ▲
                                                </button>
                                                <div className="w-px h-4 bg-neutral-800"></div>
                                                <button type="button" onClick={() => moveBlogBlock(index, 'down')} disabled={index === blogForm.contentBlocks.length - 1} className="p-1.5 text-neutral-400 hover:text-white disabled:opacity-30 transition">
                                                    ▼
                                                </button>
                                                <div className="w-px h-4 bg-neutral-800"></div>
                                                <button type="button" onClick={() => removeBlogBlock(block.id)} className="p-1.5 text-red-400 hover:text-red-300 transition">
                                                    ✕
                                                </button>
                                            </div>

                                            {/* Block Content Inputs */}
                                            {block.type === 'text' && (
                                                <div className="mt-2">
                                                    <label className="block text-sm font-medium text-neutral-400 mb-2">Paragraph Text</label>
                                                    <textarea required value={block.content} onChange={(e) => updateBlogBlock(block.id, 'content', e.target.value)} rows="5" className="w-full bg-neutral-950 border border-neutral-700 rounded p-3 text-white focus:outline-none focus:border-violet-500 transition leading-relaxed"></textarea>
                                                </div>
                                            )}

                                            {block.type === 'heading' && (
                                                <div className="mt-2">
                                                    <label className="block text-sm font-medium text-neutral-400 mb-2">Section Heading</label>
                                                    <input required type="text" value={block.content} onChange={(e) => updateBlogBlock(block.id, 'content', e.target.value)} className="w-full text-lg font-medium bg-neutral-950 border border-neutral-700 rounded p-3 text-white focus:outline-none focus:border-violet-500 transition" placeholder="Material as Narrative" />
                                                </div>
                                            )}

                                            {block.type === 'quote' && (
                                                <div className="mt-2">
                                                    <label className="block text-sm font-medium text-neutral-400 mb-2">Blockquote Content</label>
                                                    <textarea required value={block.content} onChange={(e) => updateBlogBlock(block.id, 'content', e.target.value)} rows="3" className="w-full italic text-lg bg-neutral-950 border-l-4 border-l-violet-500 border-y border-r border-neutral-700 rounded-r p-3 text-white focus:outline-none focus:border-violet-500 transition" placeholder="“Good architecture does not shout...”"></textarea>
                                                </div>
                                            )}

                                            {block.type === 'singleImage' && (
                                                <div className="mt-2 space-y-3">
                                                    <label className="block text-sm font-medium text-neutral-400">Single Image Block</label>
                                                    <input required type="text" value={block.src} onChange={(e) => updateBlogBlock(block.id, 'src', e.target.value)} className="w-full bg-neutral-950 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="Image URL (e.g. /images/blogs/concretecracking/3.webp)" />
                                                    <input type="text" value={block.caption} onChange={(e) => updateBlogBlock(block.id, 'caption', e.target.value)} className="w-full text-sm bg-neutral-950 border border-neutral-700 rounded p-2.5 text-neutral-400 focus:outline-none focus:border-violet-500 transition" placeholder="Optional Caption (e.g. Fig 2. Courtyard acting as...)" />
                                                </div>
                                            )}

                                            {block.type === 'imageGrid' && (
                                                <div className="mt-2 space-y-3">
                                                    <label className="block text-sm font-medium text-neutral-400">Two-Image Grid Block</label>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        <input required type="text" value={block.src1} onChange={(e) => updateBlogBlock(block.id, 'src1', e.target.value)} className="w-full bg-neutral-950 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="Left Image URL" />
                                                        <input required type="text" value={block.src2} onChange={(e) => updateBlogBlock(block.id, 'src2', e.target.value)} className="w-full bg-neutral-950 border border-neutral-700 rounded p-2.5 text-white focus:outline-none focus:border-violet-500 transition" placeholder="Right Image URL" />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Add Block Controls */}
                                <div className="mt-6 flex flex-wrap gap-2 pt-4 border-t border-neutral-800">
                                    <span className="text-sm text-neutral-500 py-2 mr-2">Add Content Block:</span>
                                    <button type="button" onClick={() => addBlogBlock('text')} className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 text-sm rounded transition border border-neutral-700">+ Paragraph</button>
                                    <button type="button" onClick={() => addBlogBlock('heading')} className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 text-sm rounded transition border border-neutral-700">+ Heading</button>
                                    <button type="button" onClick={() => addBlogBlock('quote')} className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 text-sm rounded transition border border-neutral-700">+ Quote</button>
                                    <button type="button" onClick={() => addBlogBlock('singleImage')} className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 text-sm rounded transition border border-neutral-700">+ Single Image</button>
                                    <button type="button" onClick={() => addBlogBlock('imageGrid')} className="px-4 py-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 text-sm rounded transition border border-neutral-700">+ Image Grid</button>
                                </div>
                            </div>

                            {/* Submit */}
                            <div className="pt-6 border-t border-neutral-800">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full py-4 rounded-lg font-semibold tracking-wide text-lg transition ${isSubmitting ? 'bg-violet-800 text-white/50 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700 text-white'}`}
                                >
                                    {isSubmitting ? 'Publishing Blog...' : 'Publish Blog to Website'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                )}
            </div>
        </motion.div>
    );
};

export default AdminDashboard;
